import {
  GetOfferingByIdForPageGql,
  GetOfferingBySlugForPageGql,
  GetOfferingPageGql,
  SiteLocale,
} from '../../../../__generated__/datocms.types'
import {mergeMap, Observable, of, throwError, zip} from 'rxjs'
import {OfferingPage} from '../types/offering-page.types'
import {Offering} from '../types/offering.types'
import {catchError, map} from 'rxjs/operators'
import {TranslocoService} from '@ngneat/transloco'
import {getSiteLocale} from '../site-locale'
import {CMS_GRAPHQL_CLIENT_NAME} from '../content-graphql-split'
import {parseOfferingPage} from '../serde/offering-page.serde'
import {Injectable} from '@angular/core'
import {parseOffering} from '../serde/offering.serde'
import {LinkService} from '../../i18n/link.service'
import {Logger} from '../../logging/logger.types'

@Injectable()
export class OfferingPageContentService {
  constructor(
    private readonly locale: TranslocoService,
    private readonly linkService: LinkService,
    private readonly logger: Logger,
    private readonly getOfferingPageGql: GetOfferingPageGql,
    private readonly getOfferingByIdGql: GetOfferingByIdForPageGql,
    private readonly getOfferingBySlugGql: GetOfferingBySlugForPageGql,
  ) {}

  private getOffering(locale: SiteLocale, idOrSlug: number | string): Observable<Offering | undefined> {
    if (Number.isInteger(Number(idOrSlug))) {
      return this.getOfferingByIdGql
        .fetch({offeringId: idOrSlug, locale}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
        .pipe(
          map(({data: {offering}}) => {
            if (!offering) {
              return undefined
            }
            return parseOffering(offering)
          }),
        )
    }

    return this.getOfferingBySlugGql
      .fetch({slug: String(idOrSlug), locale}, {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}})
      .pipe(
        map(({data: {offering}}) => {
          if (!offering) {
            return undefined
          }
          return parseOffering(offering)
        }),
      )
  }

  getOfferingPage(idOrSlug: number | string): Observable<{page: OfferingPage; offering: Offering}> {
    const locale = getSiteLocale(this.locale)
    const offering = this.getOffering(locale, idOrSlug)
    return offering.pipe(
      map(offering => {
        if (!offering) {
          throw new RangeError(`Offering with id/slug ${idOrSlug} not found`)
        }

        return offering
      }),
      mergeMap(offering => {
        if (!offering.cmsId) {
          throw new RangeError(`Offering with id/slug ${idOrSlug} has no cmsId`)
        }

        const pageResult = this.getOfferingPageGql.fetch(
          {locale: getSiteLocale(this.locale), offeringCmsId: offering.cmsId},
          {context: {clientName: CMS_GRAPHQL_CLIENT_NAME}},
        )
        return zip(pageResult, of(offering))
      }),
      map(([res, offering]) => {
        const data = res.data
        const page = data.page ?? data.defaultPage
        const knowledgeSection = data.offering?.knowledgeSection

        if (!page) {
          throw new RangeError(`no default offering page found, cms misconfiguration!`)
        }

        return {page, offering, knowledgeSection}
      }),
      map(({page, offering, knowledgeSection}) => ({
        page: parseOfferingPage(this.linkService, page, knowledgeSection),
        offering,
      })),
      catchError(err => {
        this.logger.error(`Failed to parse offering ${idOrSlug}`)
        return throwError(() => err)
      }),
    )
  }
}
